import React, { useEffect, useState } from 'react';
import type { Assay, AssayPanel } from '@mariana/labos-client';
import { limsClient, apiErrorMessage } from '../../api/limsClient';
import { parseConstantInput, withConstantValue } from '../../utils/panelDefinition';
import './AssayDefinition.css';

interface Props {
  assay: Assay;
  isAdmin: boolean;
  onSaved: () => Promise<void> | void;
}

const constantTexts = (panel: AssayPanel) =>
  Object.fromEntries(Object.entries(panel.constants ?? {}).map(([name, c]) => [name, String(c.value)]));

export const PanelDefinitionPanel: React.FC<Props> = ({ assay, isAdmin, onSaved }) => {
  const panel = assay.attributes?.panel as AssayPanel;
  const [titles, setTitles] = useState<Map<number, string>>(new Map());
  const [texts, setTexts] = useState<Record<string, string>>(() => constantTexts(panel));
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setTexts(constantTexts(panel)), [panel]);
  useEffect(() => {
    let live = true;
    limsClient.getAssays({ limit: 500 }).then(page => {
      if (!live) return;
      setTitles(new Map(page.data.flatMap(a => (typeof a.id === 'number' && typeof a.attributes?.title === 'string' ? [[a.id, a.attributes.title] as const] : []))));
    }).catch(() => undefined);
    return () => { live = false; };
  }, []);

  const title = (id: number) => `${titles.get(id) ?? 'Assay'} #${id}`;

  const save = async (name: string) => {
    const value = parseConstantInput(texts[name] ?? '');
    if (value === null) {
      setError(`${name}: enter a number`);
      return;
    }
    setSaving(name);
    setError(null);
    try {
      await limsClient.updateEntityByType('assay', assay.id!, { panel: withConstantValue(panel, name, value) });
      await onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="assay-panel">
      <h3 className="assay-panel-heading"><span>Panel</span></h3>
      <p className="assay-intro">
        A test of this assay reports nothing itself. LIMS starts the assays below on the sample, adds the reflex assays once the rule is decided,
        then completes the panel test. What it decided is on each panel test as <code>panel_reflex</code>.
      </p>
      <div className="assay-section">
        <div className="assay-section-heading">Starts</div>
        <ul>{panel.start.map(id => <li key={id}>{title(id)}</li>)}</ul>
      </div>
      {(panel.reflex ?? []).length > 0 && (
        <div className="assay-section">
          <div className="assay-section-heading">Reflex</div>
          <ul>
            {panel.reflex!.map((rule, i) => (
              <li key={i}>
                When {title(rule.when.assay)} <code>{rule.when.key}</code> {rule.when.op} <code>{rule.when.value}</code>, add {rule.add.map(title).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
      {Object.keys(panel.constants ?? {}).length > 0 && (
        <div className="assay-section">
          <div className="assay-section-heading">Constants</div>
          <div className="assay-form">
            {Object.entries(panel.constants!).map(([name, c]) => (
              <React.Fragment key={name}>
                <label htmlFor={`panel-constant-${name}`}>{name}</label>
                <div>
                  <div className="assay-actions" style={{ marginTop: 0 }}>
                    <input
                      id={`panel-constant-${name}`}
                      className="assay-input"
                      style={{ maxWidth: '8rem' }}
                      inputMode="decimal"
                      value={texts[name] ?? ''}
                      disabled={!isAdmin || saving !== null}
                      onChange={e => setTexts({ ...texts, [name]: e.target.value })}
                    />
                    <span className="assay-muted">{c.unit ?? ''}</span>
                    {isAdmin && texts[name] !== String(c.value) && (
                      <button type="button" className="assay-btn small primary" disabled={saving !== null} onClick={() => save(name)}>
                        {saving === name ? 'Saving…' : 'Save'}
                      </button>
                    )}
                  </div>
                  {c.description && <div className="assay-note">{c.description}</div>}
                </div>
              </React.Fragment>
            ))}
          </div>
          {!isAdmin && <div className="assay-note">An admin can change a threshold.</div>}
        </div>
      )}
      {error && <div className="assay-error">{error}</div>}
    </div>
  );
};
